import {useState} from 'react'
import {useToast} from '@/hooks/use-toast'

export interface FormSubmitField {
    id: string
    name?: string
    label: string
    value: unknown
    required?: boolean
    focusId?: string
    validate?: (value: unknown) => string | null | undefined
}

export interface UseFormSubmitOptions {
    fields: FormSubmitField[]
    onSubmit: () => void | Promise<void>
}

export function useFormSubmit({fields, onSubmit}: UseFormSubmitOptions) {
    const {toast} = useToast()
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

    const submit = async (e?: React.FormEvent) => {
        if (e) {
            e.preventDefault()
        }

        const errors: Record<string, string> = {}
        const missingLabels: string[] = []
        let firstInvalidField: FormSubmitField | null = null

        for (const field of fields) {
            let errorMsg: string | null = null

            if (field.required) {
                const val = typeof field.value === 'string' ? field.value.trim() : field.value
                if (val === undefined || val === null || val === '') {
                    // eslint-disable-next-line i18next/no-literal-string -- standard validation error
                    errorMsg = 'This field is required.'
                    missingLabels.push(field.label)
                }
            }

            if (!errorMsg && field.validate) {
                errorMsg = field.validate(field.value) ?? null
                if (errorMsg) {
                    missingLabels.push(field.label)
                }
            }

            if (errorMsg) {
                errors[field.id] = errorMsg
                if (!firstInvalidField) {
                    firstInvalidField = field
                }
            }
        }

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors)

            // eslint-disable-next-line i18next/no-literal-string -- validation error message
            toast({
                variant: 'destructive',
                description: `The following fields are required: ${missingLabels.join(', ')}`,
            })

            if (firstInvalidField) {
                const targetId = firstInvalidField.focusId || firstInvalidField.id
                setTimeout(() => {
                    const el = document.getElementById(targetId) ||
                        document.querySelector(`[id="${targetId}"]`) ||
                        document.querySelector(`[name="${targetId}"]`) ||
                        document.querySelector(`[data-testid="${targetId}"]`) ||
                        document.querySelector(`[data-testid*="${targetId}"]`)
                    console.log('useFormSubmit: targetId =', targetId, 'element found =', !!el)
                    if (el) {
                        (el as HTMLElement).focus()
                    }
                }, 0)
            }
            return
        }

        setFieldErrors({})
        await onSubmit()
    }

    return {
        submit,
        fieldErrors,
        setFieldErrors,
    }
}
